import { useState } from "react";
import { Input } from "@/components/ui/input";
import { ArrowRight, Check } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailCaptureSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const { data } = await axios.post(`${API}/waitlist`, { email });
      setStatus("success");
      setMessage(data.message);
      setEmail("");
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setMessage(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    }
  };

  return (
    <section
      id="waitlist"
      data-testid="email-capture-section"
      className="py-24 sm:py-32 px-6"
      style={{ backgroundColor: "var(--white)" }}
    >
      <div className="max-w-xl mx-auto text-center">
        <h2
          data-testid="email-capture-heading"
          className="animate-fade-up"
          style={{
            fontFamily: "var(--font-serif)",
            fontSize: "clamp(1.75rem, 3.5vw, 2.5rem)",
            fontWeight: 600,
            color: "var(--charcoal)",
            marginBottom: "0.75rem",
            letterSpacing: "-0.01em",
          }}
        >
          Be part of what we're building
        </h2>

        <p
          data-testid="email-capture-subheading"
          className="animate-fade-up delay-100"
          style={{
            fontFamily: "var(--font-sans)",
            fontSize: "1.05rem",
            lineHeight: 1.65,
            color: "var(--charcoal-light)",
            marginBottom: "2rem",
          }}
        >
          Get early access to Resonance and hear about new products before
          anyone else.
        </p>

        {status === "success" ? (
          <div
            data-testid="email-capture-success"
            className="animate-fade-up flex items-center justify-center gap-3"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "1.05rem",
              color: "var(--coral)",
              fontWeight: 500,
              padding: "1rem",
            }}
          >
            <Check size={20} strokeWidth={2.5} />
            <span>{message}</span>
          </div>
        ) : (
          <form
            data-testid="email-capture-form"
            onSubmit={handleSubmit}
            className="animate-fade-up delay-200 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <Input
              data-testid="email-capture-input"
              type="email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              required
              className="flex-1 h-12 px-4 rounded-full text-base"
              style={{
                fontFamily: "var(--font-sans)",
                backgroundColor: "var(--blush)",
                border: "1px solid rgba(44,44,42,0.1)",
                borderRadius: "50px",
              }}
            />
            <button
              data-testid="email-capture-submit"
              type="submit"
              disabled={status === "loading"}
              className="btn-coral flex items-center justify-center gap-2"
              style={{
                padding: "12px 28px",
                fontSize: "0.95rem",
                opacity: status === "loading" ? 0.7 : 1,
                whiteSpace: "nowrap",
              }}
            >
              {status === "loading" ? "Joining..." : "Join the waitlist"}
              {status !== "loading" && <ArrowRight size={16} />}
            </button>
          </form>
        )}

        {status === "error" && (
          <p
            data-testid="email-capture-error"
            className="mt-3"
            style={{
              fontFamily: "var(--font-sans)",
              fontSize: "0.875rem",
              color: "#c53030",
            }}
          >
            {message}
          </p>
        )}
      </div>
    </section>
  );
}
